<script setup>
import { ref } from 'vue';
import InboxReconnectionRequired from '../../components/InboxReconnectionRequired.vue';
import Inboxes from 'dashboard/api/inboxes';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';

const props = defineProps({
  inbox: {
    type: Object,
    default: () => ({}),
  },
});

const { t } = useI18n();

const showModal = ref(false);
const step = ref('init'); // 'init' | 'verify_code' | 'verify_2fa'
const verificationCode = ref('');
const password2FA = ref('');
const isRequestingCode = ref(false);
const isSubmittingCode = ref(false);

function openReauthorizeModal() {
  showModal.value = true;
  step.value = 'init';
  verificationCode.value = '';
  password2FA.value = '';
}

function closeModal() {
  showModal.value = false;
}

async function sendCode() {
  isRequestingCode.value = true;
  try {
    const res = await Inboxes.eitaaSendCode(props.inbox.id);
    const data = res.data;
    if (data && (data.status === 'code_sent' || data.status === 'success' || data.status === 'already_authorized')) {
      if (data.status === 'success' || data.status === 'already_authorized') {
        useAlert(t('INBOX_MGMT.ADD.EITAA_CHANNEL.ALREADY_CONNECTED'));
        props.inbox.reauthorization_required = false;
        closeModal();
      } else {
        useAlert(t('INBOX_MGMT.ADD.EITAA_CHANNEL.CODE_SENT_SUCCESS'));
        step.value = 'verify_code';
      }
    } else {
      throw new Error(data?.detail || 'Failed to request code');
    }
  } catch (error) {
    useAlert(error.message || 'Could not request code from Eitaa connector');
  } finally {
    isRequestingCode.value = false;
  }
}

async function verifyCode() {
  if (!verificationCode.value.trim()) return;
  isSubmittingCode.value = true;
  try {
    const res = await Inboxes.eitaaVerify(props.inbox.id, verificationCode.value.trim());
    const data = res.data;
    if (data && data.status === 'success') {
      useAlert(t('INBOX_MGMT.ADD.EITAA_CHANNEL.AUTH_SUCCESS'));
      props.inbox.reauthorization_required = false;
      closeModal();
    } else if (data && data.status === 'requires_2fa') {
      useAlert(t('INBOX_MGMT.ADD.EITAA_CHANNEL.REQUIRES_2FA'));
      step.value = 'verify_2fa';
    } else {
      throw new Error(data?.detail || 'Invalid code');
    }
  } catch (error) {
    useAlert(error.message || 'Verification code submission failed');
  } finally {
    isSubmittingCode.value = false;
  }
}

async function verify2FA() {
  if (!password2FA.value.trim()) return;
  isSubmittingCode.value = true;
  try {
    const res = await Inboxes.eitaaVerify2FA(props.inbox.id, password2FA.value.trim());
    const data = res.data;
    if (data && data.status === 'success') {
      useAlert(t('INBOX_MGMT.ADD.EITAA_CHANNEL.AUTH_SUCCESS'));
      props.inbox.reauthorization_required = false;
      closeModal();
    } else {
      throw new Error(data?.detail || 'Invalid 2FA password');
    }
  } catch (error) {
    useAlert(error.message || '2FA verification failed');
  } finally {
    isSubmittingCode.value = false;
  }
}
</script>

<template>
  <div>
    <InboxReconnectionRequired
      class="mx-6"
      :description="t('INBOX_MGMT.EITAA_RECONNECTION_REQUIRED')"
      @reauthorize="openReauthorizeModal"
    />

    <woot-modal :show="showModal" @close="closeModal">
      <div class="flex flex-col h-auto overflow-auto p-8">
        <woot-modal-header
          :header-title="t('INBOX_MGMT.ADD.EITAA_CHANNEL.TITLE')"
          :header-content="t('INBOX_MGMT.EITAA_RECONNECT_DESC') + ': ' + inbox.phone_number"
        />

        <!-- STEP 1: INIT / REQUEST CODE -->
        <div v-if="step === 'init'" class="w-full flex flex-col mt-4">
          <p class="text-sm text-slate-600 mb-6">
            {{ t('INBOX_MGMT.EITAA_RECONNECT_STEP1') }}
          </p>
          <woot-button
            :is-loading="isRequestingCode"
            solid
            blue
            @click="sendCode"
          >
            {{ t('INBOX_MGMT.ADD.EITAA_CHANNEL.SUBMIT_BUTTON') }}
          </woot-button>
        </div>

        <!-- STEP 2: VERIFY CODE -->
        <div v-if="step === 'verify_code'" class="w-full flex flex-col mt-4">
          <label>
            {{ t('INBOX_MGMT.ADD.EITAA_CHANNEL.VERIFY_CODE.LABEL') }}
            <input
              v-model="verificationCode"
              type="text"
              class="mt-2"
              :placeholder="t('INBOX_MGMT.ADD.EITAA_CHANNEL.VERIFY_CODE.PLACEHOLDER')"
              @keyup.enter="verifyCode"
            />
          </label>
          <p class="help-text text-xs text-slate-500 mt-1 mb-6">
            {{ t('INBOX_MGMT.ADD.EITAA_CHANNEL.VERIFY_CODE.SUBTITLE') }}
          </p>
          <div class="flex gap-4">
            <woot-button
              :is-loading="isSubmittingCode"
              solid
              blue
              @click="verifyCode"
            >
              {{ t('INBOX_MGMT.ADD.EITAA_CHANNEL.VERIFY_CODE.SUBMIT') }}
            </woot-button>
            <woot-button
              variant="link"
              :is-loading="isRequestingCode"
              @click="sendCode"
            >
              {{ t('INBOX_MGMT.ADD.EITAA_CHANNEL.VERIFY_CODE.RESEND') }}
            </woot-button>
          </div>
        </div>

        <!-- STEP 3: VERIFY 2FA -->
        <div v-if="step === 'verify_2fa'" class="w-full flex flex-col mt-4">
          <label>
            {{ t('INBOX_MGMT.ADD.EITAA_CHANNEL.PASSWORD_2FA.LABEL') }}
            <input
              v-model="password2FA"
              type="password"
              class="mt-2"
              :placeholder="t('INBOX_MGMT.ADD.EITAA_CHANNEL.PASSWORD_2FA.PLACEHOLDER')"
              @keyup.enter="verify2FA"
            />
          </label>
          <p class="help-text text-xs text-slate-500 mt-1 mb-6">
            {{ t('INBOX_MGMT.ADD.EITAA_CHANNEL.PASSWORD_2FA.SUBTITLE') }}
          </p>
          <woot-button
            :is-loading="isSubmittingCode"
            solid
            blue
            @click="verify2FA"
          >
            {{ t('INBOX_MGMT.ADD.EITAA_CHANNEL.PASSWORD_2FA.SUBMIT') }}
          </woot-button>
        </div>
      </div>
    </woot-modal>
  </div>
</template>
